import {
  BUILDING_STATUS_OPTIONS,
  PROPERTY_REVIEW_FORM_NUMBER_MAP,
  PROPERTY_REVIEW_TYPE_OPTIONS,
} from './constants';

const INVALID_APPRAISAL_FILE_ID_MESSAGE =
  'Please enter a File Id that is 10 characters in length. This cannot include any spaces or special characters and must only be letters and numbers.';

export const INVALID_ACREAGE_MESSAGE = 'Number of Acres cannot exceed 999.';

export const isValidAppraisalFileId = (value: string | null | undefined) => {
  if (!value) {
    return true;
  }

  return /^[A-Za-z0-9]{10}$/.test(value);
};

export const isValidAcreField = (value: number | null | undefined) => {
  if (value == null) {
    return true;
  }

  return value <= 999;
};

const sanitizeAppraisalFileId = (value: string) =>
  value.replaceAll(/[^A-Za-z0-9]/g, '').slice(0, 10);

const toNullableNumber = (value: string | null | undefined) =>
  value === '' || value == null ? null : Number(value);

type BuildAppraisalInformationSectionParams = {
  handleChange: (field: string, value: unknown) => void;
  setValue: (name: string, value: unknown, options?: unknown) => void;
  trigger: (name?: string) => Promise<boolean>;
  sizeMeasurement: string;
  isAppraisedValueReadOnly: boolean;
  watch: (field: string) => unknown;
};

export const buildAppraisalInformationSection = ({
  handleChange,
  setValue,
  trigger,
  sizeMeasurement,
  isAppraisedValueReadOnly,
  watch,
}: BuildAppraisalInformationSectionParams) => [
  {
    type: 'select',
    name: 'propertyValueFormType',
    label: 'Property Review Type',
    dataTestId: 'propertyValueFormType',
    options: PROPERTY_REVIEW_TYPE_OPTIONS,
    onChangeHandler: (value) => {
      const numericValue = +value;
      handleChange('propertyValueFormType', numericValue);
      handleChange(
        'propertyReviewFormNum',
        PROPERTY_REVIEW_FORM_NUMBER_MAP[numericValue] ?? null,
      );
    },
    span: 3,
  },
  {
    type: 'text',
    name: 'propertyReviewFormNum',
    label: 'Property Review Form Number',
    dataTestId: 'propertyReviewFormNum',
    placeholder: 'Property Review Form Number',
    readOnly: true,
    span: 2,
  },
  {
    type: 'text',
    name: 'appraisalFileIdNum',
    label: 'Appraisal File ID',
    dataTestId: 'appraisalFileIdNum',
    placeholder: 'Appraisal File ID',
    span: 2,
    onChangeHandler: (e) => {
      const nextValue = sanitizeAppraisalFileId(e.target.value ?? '');
      setValue('appraisalFileIdNum', nextValue, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    onBlur: () => {
      trigger('appraisalFileIdNum');
    },
    rules: {
      validate: (value: string) =>
        isValidAppraisalFileId(value) || INVALID_APPRAISAL_FILE_ID_MESSAGE,
    },
  },
  {
    type: 'currency',
    name: 'appraisedValue',
    label: 'Appraised Value',
    dataTestId: 'appraisedValue',
    placeholder: 'Appraised Value',
    value: watch('appraisedValue'),
    onChangeHandler: (value) => handleChange('appraisedValue', value),
    readOnly: isAppraisedValueReadOnly,
    span: 2,
  },
  {
    type: 'radioGroup',
    name: 'sizeMeasurement',
    value: 'sizeMeasurement',
    label: 'Size Measurement',
    dataTestId: 'sizeMeasurement',
    options: [
      { value: 'squareFootage', label: 'Square Ft' },
      { value: 'numberOfAcres', label: 'Acres' },
    ],
    onChangeHandler: (value) => {
      handleChange('sizeMeasurement', value);
      if (value === 'squareFootage') {
        setValue('numberOfAcres', null, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      if (value === 'numberOfAcres') {
        setValue('squareFootage', null, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    },
    span: 1,
    marginTop: '0.2rem',
  },
  {
    type: 'number',
    name: 'squareFootage',
    label: 'Square Footage',
    dataTestId: 'squareFootage',
    placeholder: 'Square Footage',
    value: watch('squareFootage'),
    onChangeHandler: (e) =>
      handleChange('squareFootage', toNullableNumber(e.target.value)),
    span: 2,
    isHidden: () => sizeMeasurement === 'numberOfAcres',
  },
  {
    type: 'number',
    name: 'numberOfAcres',
    label: 'Number of Acres',
    dataTestId: 'numberOfAcres',
    placeholder: 'Number of Acres',
    value: watch('numberOfAcres'),
    onChangeHandler: (e) =>
      setValue('numberOfAcres', toNullableNumber(e.target.value), {
        shouldValidate: true,
        shouldDirty: true,
      }),
    span: 2,
    isHidden: () => sizeMeasurement === 'squareFootage',
    onBlur: () => {
      trigger('numberOfAcres');
    },
  },
  {
    type: 'select',
    name: 'buildingStatus',
    label: 'Building Status',
    dataTestId: 'buildingStatus',
    options: BUILDING_STATUS_OPTIONS,
    onChangeHandler: (value) => handleChange('buildingStatus', value),
    span: 2,
  },
];
